import React from 'react';
import PropTypes from 'prop-types';

import {
  ContentBanner,
  mapContentfulCta,
} from 'public/components/ContentBanner';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import { optimizeContentfulImageUrl } from 'common/components';

import useCustomStyle from '../../hooks';
import { getStylesByPrefix, toStyleObject } from '../../utils';

function getAssetUrl(asset) {
  if (!asset?.fields?.file) return undefined;
  return optimizeContentfulImageUrl(
    asset.fields.file.url,
    asset.fields.file.contentType
  );
}

function getBackgroundUrl(background, ...keys) {
  if (!background?.fields) return undefined;
  if (background.fields.file) {
    return optimizeContentfulImageUrl(
      background.fields.file.url,
      background.fields.file.contentType
    );
  }

  for (const key of keys) {
    const url = getAssetUrl(background.fields[key]);
    if (url) return url;
  }

  return undefined;
}

export default function CLPContentBanner({ component, handleTrack }) {
  const {
    title,
    content,
    background,
    cta,
    ctas,
    size,
    contentPosition,
    fullWidthMobile,
  } = component.fields;
  const [, style] = useCustomStyle(component.fields.settings);
  const bannerCtas = (ctas?.length ? ctas : [cta])
    .map(mapContentfulCta)
    .filter(Boolean);

  return (
    <ContentBanner
      title={title}
      description={content}
      size={style['--banner-size'] || size}
      contentPosition={style['--banner-content-position'] || contentPosition}
      backgroundImage={getBackgroundUrl(background, 'desktop', 'tablet')}
      mobileBackgroundImage={getBackgroundUrl(background, 'mobile')}
      fullWidthMobile={style['--banner-full-width-mobile'] || fullWidthMobile}
      ctas={bannerCtas}
      style={toStyleObject({
        ...style,
        ...getStylesByPrefix('--container-', style),
      })}
      contentStyle={toStyleObject(getStylesByPrefix('--content-', style))}
      titleStyle={toStyleObject(getStylesByPrefix('--title-', style))}
      descriptionStyle={toStyleObject(
        getStylesByPrefix('--description-', style)
      )}
      ctaStyle={toStyleObject(getStylesByPrefix('--cta-', style))}
      onCtaClick={(event, clicked) =>
        handleTrack({
          event: STANDARD_EVENTS.click,
          params: {
            label: clicked.label,
            cta_type: clicked.type === 'jump' ? CTA_TYPES.jump : CTA_TYPES.link,
            destination_url: clicked.url,
          },
        })
      }
    />
  );
}

CLPContentBanner.propTypes = {
  component: PropTypes.object.isRequired,
  handleTrack: PropTypes.func,
};

CLPContentBanner.defaultProps = {
  handleTrack: () => {},
};
